export type SuccessStoryTheme = 'blue' | 'purple' | 'orange' | 'grey';

type StoryStat = {
  iconSrc: string;
  /**
   * Big stat value, e.g. "12x ROI" or "$2.0M". Rendered as a single line; the
   * smaller suffix can be split out via `valueSuffix` to match the Figma
   * type-scale where the trailing unit drops one step.
   */
  value: string;
  valueSuffix?: string;
  caption: string;
};

type StoryFlag = {
  src: string;
  alt: string;
};

type StoryPerson = {
  name: string;
  title: string;
  imageSrc: string;
  /** Optional `object-position` for the desktop card image. Defaults to center. */
  imagePosition?: string;
};

type FeatureStory = {
  kind: 'feature';
  id: string;
  theme: Exclude<SuccessStoryTheme, 'grey'>;
  /** Show the photo on the left (default) or right on desktop. */
  imageSide: 'left' | 'right';
  heading: string;
  description?: string;
  stat: StoryStat;
  flag: StoryFlag;
  quote: string;
  person: StoryPerson;
};

type QuoteStory = {
  kind: 'quote';
  id: string;
  theme: 'grey';
  heading: string;
  flag: StoryFlag;
  quote: string;
  attribution: {
    name: string;
    title: string;
  };
};

export type SuccessStory = FeatureStory | QuoteStory;

export const successStoriesContent = {
  heading: 'Success stories',
  description:
    'Today’s most innovative companies don’t just hire for skills, they build them. With Udacity, learners gain AI-ready expertise through hands-on projects, expert mentorship, and programs proven to deliver business impact.',
};

const miskAcademyAttribution = {
  name: 'Abdurrahman Alshanquiti',
  title: 'Misk Academy x Udacity Student, Digital Marketing Nanodegree program',
} as const;

export const successStoriesItems: SuccessStory[] = [
  {
    kind: 'feature',
    id: 'egypt',
    theme: 'blue',
    imageSide: 'left',
    heading: 'Fueling economic growth in Egypt',
    description:
      'Udacity’s partnership with Egypt enabled citizens to win high-paying roles in government by developing software development talent.',
    stat: {
      iconSrc: '/images/government/success-stories/icon-roi.svg',
      value: '12x',
      valueSuffix: ' ROI',
      caption: 'for the Egyptian government',
    },
    flag: {
      src: '/images/government/success-stories/flag-egypt.svg',
      alt: 'Flag of Egypt',
    },
    quote:
      '“We’re bringing…know-how to our young population to prepare them to compete in the global market. This program is…about ensuring that graduates improve their livelihoods and, in turn, benefit the economy and transform Egypt into a tech hub.”',
    person: {
      name: 'Dr. Amr Talaat',
      title: 'Former Egypt’s Minister of Communications and Information Technology',
      imageSrc: '/images/government/success-stories/amr-talaat.jpg',
      imagePosition: 'center 20%',
    },
  },
  {
    kind: 'feature',
    id: 'mozambique',
    theme: 'purple',
    imageSide: 'right',
    heading: 'Turning curiosity into a career in business analytics',
    description:
      'With Vodacom Mozambique x Udacity scholarship, the program has: strengthened the local tech talent pipeline, contributed to digital transformation efforts in key industries and inspired future initiatives focused on women in tech and underserved communities.',
    stat: {
      iconSrc: '/images/government/success-stories/icon-nps.svg',
      value: '95',
      valueSuffix: ' NPS',
      caption: 'reflecting exceptional satisfaction from participants',
    },
    flag: {
      src: '/images/government/success-stories/flag-mozambique.svg',
      alt: 'Flag of Mozambique',
    },
    quote:
      '“Before Udacity, I understood business concepts but lacked the technical skills and real-world experience needed to break into business analytics. The mentorship and practical learning helped me grow not only in skills but in confidence, giving me the tools to pursue opportunities in business analytics and prove what I can do.”',
    person: {
      name: 'Esmenia Macome',
      title: 'Udacity student, Business Analytics ND',
      imageSrc: '/images/government/success-stories/esmenia-macome.jpg',
      imagePosition: 'center',
    },
  },
  {
    kind: 'feature',
    id: 'brazil',
    theme: 'orange',
    imageSide: 'left',
    heading: 'Turning AI skills into a $2M national sustainability project',
    stat: {
      iconSrc: '/images/government/success-stories/icon-money.svg',
      value: '$2.0',
      valueSuffix: 'M',
      caption: 'approved sustainability initiative for Brazil’s Northeast',
    },
    flag: {
      src: '/images/government/success-stories/flag-brazil.svg',
      alt: 'Flag of Brazil',
    },
    quote:
      '“Before Udacity, I was stuck in a dead-end job with no growth. Udacity helped me build deep, professional AI expertise and the confidence and maturity to communicate it so well that people now call me “Professor.” My biggest achievement is that I developed a federal project proposal that was approved as a ~$2M USD sustainability initiative for Brazil’s Northeast, something that would have been impossible to achieve without the skills and vision Udacity gave me.”',
    person: {
      name: 'Flora Belas',
      title: 'Udacity Student, AI Programming with Python',
      imageSrc: '/images/government/success-stories/flora-belas.png',
      imagePosition: 'top',
    },
  },
  {
    kind: 'quote',
    id: 'oman',
    theme: 'grey',
    heading: 'From interview struggles to prestigious job',
    flag: {
      src: '/images/government/success-stories/flag-oman.svg',
      alt: 'Flag of Oman',
    },
    quote:
      '“Before Udacity, I struggled to get interviews - and when I did, I wasn’t competitive. After I started Udacity courses, my life changed significantly. I gained the skills and confidence I was lacking, started getting more interviews, and got a prestigious job that improved my financial and social situation. Now I feel like I can achieve anything I aspire to.”',
    attribution: {
      name: 'Salima AlShabibi',
      title: 'Makeen x Udacity Student, Business Analytics Nanodegree program',
    },
  },
  {
    kind: 'quote',
    id: 'saudi-arabia',
    theme: 'grey',
    heading: 'Empowering employment in Saudi Arabia',
    flag: {
      src: '/images/government/success-stories/flag-saudi-arabia.svg',
      alt: 'Flag of Saudi Arabia',
    },
    quote:
      '“Nothing compares to…Udacity courses. These opportunities helped me ace an elite full-time job as a digital marketing manager. I am working…to help new generations graduate from Nanodegree programs and find proper jobs.”',
    attribution: miskAcademyAttribution,
  },
];
